<template>
  <section id="proposal-vs-contract" class="pvc">
    <div class="pvc-inner" :class="{ 'pvc-inner--template': layout === 'template' }">
      <h2>{{ copy.title }}</h2>
      <p class="pvc-copy">{{ copy.intro }}</p>
      <div class="pvc-table-wrap">
        <table class="pvc-table">
          <thead>
            <tr><th scope="col"><span class="sr-only">Question</span></th><th scope="col">Proposal</th><th scope="col">Contract</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in copy.rows" :key="row.label">
              <th scope="row">{{ row.label }}</th>
              <td><span class="pvc-cell-label">Proposal</span>{{ row.proposal }}</td>
              <td><span class="pvc-cell-label">Contract</span>{{ row.contract }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <h3>{{ copy.bothTitle }}</h3>
      <p class="pvc-copy">{{ copy.both }}</p>
      <div class="pvc-example">
        <h3>{{ copy.exampleTitle }}</h3>
        <p>{{ copy.example }}</p>
        <NuxtLink :to="localePath(copy.link.path)">{{ copy.link.label }} <span aria-hidden="true">→</span></NuxtLink>
      </div>
    </div>
  </section>
</template>

<script setup>
import { proposalVsContract as copy } from '~/data/proposal-vs-contract';

// 'tool' lines up with ToolShell's 66rem sections; 'template' with FreeTemplatePage's max-w-6xl.
defineProps({ layout: { type: String, default: 'tool' } });

const { localePath } = useWorkspaceLocale();
</script>

<style scoped>
.pvc {
  border-top: 1px solid #dbe4ee;
  background: #ffffff;
  padding: 3.5rem 0;
}

.pvc-inner {
  max-width: 66rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}

@media (min-width: 640px) {
  .pvc-inner { padding-inline: 1.75rem; }
}

.pvc-inner--template {
  max-width: 72rem;
  padding-inline: 1rem;
}

@media (min-width: 640px) {
  .pvc-inner--template { padding-inline: 1.5rem; }
}

.pvc h2 {
  color: #0f172a;
  font-size: clamp(1.5rem, 2.8vw, 2.1rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.pvc h3 {
  margin-top: 1.75rem;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 800;
}

.pvc-copy {
  max-width: 48rem;
  margin-top: 0.8rem;
  color: #475569;
  font-size: 0.98rem;
  line-height: 1.7;
}

.pvc-table-wrap {
  max-width: 56rem;
  margin-top: 1.5rem;
  border: 1px solid #dbe4ee;
  border-radius: 0.75rem;
  overflow-x: auto;
}

.pvc-table {
  width: 100%;
  min-width: 30rem;
  border-collapse: collapse;
  font-size: 0.9rem;
  line-height: 1.55;
}

.pvc-table th,
.pvc-table td {
  border-bottom: 1px solid #e8eef5;
  padding: 0.75rem 1rem;
  text-align: left;
  vertical-align: top;
}

.pvc-table thead th {
  background: #f8fafc;
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.pvc-table tbody th {
  color: #0f172a;
  font-weight: 750;
  white-space: nowrap;
}

.pvc-table td {
  color: #475569;
}

.pvc-cell-label {
  display: none;
}

@media (max-width: 640px) {
  .pvc-table { min-width: 0; }
  .pvc-table thead { display: none; }
  .pvc-table tbody tr { display: block; border-bottom: 1px solid #e8eef5; padding: 0.4rem 0; }
  .pvc-table tbody tr:last-child { border-bottom: 0; }
  .pvc-table tbody th,
  .pvc-table td { display: block; border-bottom: 0; padding: 0.3rem 1rem; white-space: normal; }
  .pvc-cell-label {
    display: block;
    color: #0e7490;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
}

.pvc-table tbody tr:last-child th,
.pvc-table tbody tr:last-child td {
  border-bottom: 0;
}

.pvc-example {
  max-width: 48rem;
  margin-top: 1.75rem;
  border-left: 3px solid #0e7490;
  border-radius: 0.5rem;
  background: #f0f9fc;
  padding: 1rem 1.25rem;
}

.pvc-example h3 {
  margin-top: 0;
}

.pvc-example p {
  margin-top: 0.4rem;
  color: #475569;
  font-size: 0.95rem;
  line-height: 1.7;
}

.pvc-example a {
  display: inline-block;
  margin-top: 0.6rem;
  color: #0e7490;
  font-weight: 750;
}

:global(.dark .pvc) {
  border-color: #243247;
  background: #020617;
}

:global(.dark .pvc h2),
:global(.dark .pvc h3),
:global(.dark .pvc-table thead th),
:global(.dark .pvc-table tbody th) {
  color: #f1f5f9;
}

:global(.dark .pvc-copy),
:global(.dark .pvc-table td),
:global(.dark .pvc-example p) {
  color: #cbd5e1;
}

:global(.dark .pvc-table-wrap) {
  border-color: #243247;
}

:global(.dark .pvc-table th),
:global(.dark .pvc-table td),
:global(.dark .pvc-table tbody tr) {
  border-color: #1e293b;
}

:global(.dark .pvc-table thead th) {
  background: #0a1220;
}

:global(.dark .pvc-example) {
  background: #0b2530;
}

:global(.dark .pvc-example a),
:global(.dark .pvc-cell-label) {
  color: #67e8f9;
}
</style>
